import { useState, useEffect, useCallback } from "react";
import { useClub } from "@/pages/ClubLayout";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Layers, Pencil, Trash2, X, ArrowUp, ArrowDown } from "lucide-react";

const AUTH = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("cadii_club_token") || ""}`,
});

// HCP display: negative floats represent plus-handicaps. Same convention we
// use in wagers/players — stored as -1.1, shown as "+1.1".
function fmtHi(v) {
  if (v == null) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  if (n < 0) return `+${Math.abs(n).toFixed(1)}`;
  return n.toFixed(1);
}

// Tee colours a category can play from. Keys stored lowercase; each entry
// carries its display label + swatch styling (bg + text) so both the modal
// selector and the list badges look identical.
const TEE_COLORS = [
  { key: "negras",  label: "Negras",  bg: "#0B1D33", fg: "#F4F1EC" },
  { key: "azules",  label: "Azules",  bg: "#49607A", fg: "#F4F1EC" },
  { key: "blancas", label: "Blancas", bg: "#F4F1EC", fg: "#0B1D33", border: "#C7C9C3" },
  { key: "amarillas", label: "Amarillas", bg: "#E8C76A", fg: "#0B1D33" },
  { key: "doradas", label: "Doradas", bg: "#CBB68B", fg: "#0B1D33" },
  { key: "rojas",   label: "Rojas",   bg: "#6E1F2B", fg: "#F4F1EC" },
  { key: "verdes",  label: "Verdes",  bg: "#2E4834", fg: "#F4F1EC" },
];

export default function ClubCategories() {
  const ctx = useClub();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API}/club/categories`, { credentials: "include", headers: AUTH() });
      if (res.ok) setCategories(await res.json());
    } catch {
      toast.error("Error al cargar categorias");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const closeForm = () => { setEditCat(null); setShowForm(false); };

  const openEdit = (c) => { setEditCat(c); setShowForm(true); };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`${API}/club/categories/${deleteTarget}`, {
        method: "DELETE", credentials: "include", headers: AUTH(),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Error");
      }
      toast.success("Categoria eliminada");
      setDeleteTarget(null);
      fetchCategories();
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Error al eliminar");
    }
  };

  const handleReorder = async (catId, direction) => {
    const idx = categories.findIndex((c) => c.category_id === catId);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= categories.length) return;

    const newOrder = categories.map((c) => c.category_id);
    [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];

    try {
      await fetch(`${API}/club/categories/reorder`, {
        method: "PUT", credentials: "include", headers: AUTH(),
        body: JSON.stringify({ order: newOrder }),
      });
      fetchCategories();
    } catch {
      toast.error("Error al reordenar");
    }
  };

  const handleToggle = async (cat) => {
    const newStatus = cat.status === "active" ? "inactive" : "active";
    try {
      await fetch(`${API}/club/categories/${cat.category_id}`, {
        method: "PUT", credentials: "include", headers: AUTH(),
        body: JSON.stringify({ status: newStatus }),
      });
      toast.success(newStatus === "active" ? "Categoria activada" : "Categoria desactivada");
      fetchCategories();
    } catch {
      toast.error("Error al cambiar status");
    }
  };

  const activeCats = categories.filter((c) => c.status === "active");
  const inactiveCats = categories.filter((c) => c.status === "inactive");

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2" data-testid="club-categories-title">
            <Layers className="w-6 h-6 text-[#1B4D3E]" strokeWidth={1.5} />
            Categorias por Index
          </h1>
          <p className="text-sm text-[#64748B] mt-1">Clasificacion automatica de jugadores — {ctx?.club?.name}</p>
        </div>
        <Button onClick={() => { closeForm(); setShowForm(true); }} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="add-category-btn">
          <Plus className="w-4 h-4 mr-2" /> Nueva Categoria
        </Button>
      </div>

      {showForm && (
        <CategoryForm
          category={editCat}
          onClose={closeForm}
          onSaved={() => { closeForm(); fetchCategories(); }}
        />
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Active categories */}
          <div className="space-y-3 mb-8" data-testid="categories-list">
            {activeCats.map((cat, idx) => (
              <Card key={cat.category_id} className="card-default" data-testid={`cat-${cat.category_id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => handleReorder(cat.category_id, "up")}
                        disabled={idx === 0}
                        className="text-[#94A3B8] hover:text-[#0F172A] disabled:opacity-30"
                        data-testid={`cat-up-${cat.category_id}`}
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleReorder(cat.category_id, "down")}
                        disabled={idx === activeCats.length - 1}
                        className="text-[#94A3B8] hover:text-[#0F172A] disabled:opacity-30"
                        data-testid={`cat-down-${cat.category_id}`}
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-[#0F172A]">{cat.name}</p>
                        {cat.is_senior && (
                          <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#49607A] text-white" data-testid={`cat-tag-senior-${cat.category_id}`}>Senior</span>
                        )}
                        {cat.is_ladies && (
                          <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#CBB68B] text-[#0B1D33]" data-testid={`cat-tag-ladies-${cat.category_id}`}>Damas</span>
                        )}
                        {(cat.tees || []).map((k) => {
                          const t = TEE_COLORS.find((c) => c.key === k);
                          if (!t) return null;
                          return (
                            <span
                              key={k}
                              data-testid={`cat-tag-tee-${cat.category_id}-${k}`}
                              className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full"
                              style={{ backgroundColor: t.bg, color: t.fg, border: t.border ? `1px solid ${t.border}` : "none" }}
                            >
                              {t.label}
                            </span>
                          );
                        })}
                      </div>
                      <p className="text-sm text-[#64748B]">
                        {cat.min_index == null ? "≤" : `${fmtHi(cat.min_index)} —`}{" "}
                        {cat.max_index == null ? "sin limite" : fmtHi(cat.max_index)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700">Activa</span>
                    <Button variant="ghost" size="sm" onClick={() => handleToggle(cat)} className="text-xs text-[#64748B]">
                      Desactivar
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(cat)} data-testid={`edit-cat-${cat.category_id}`}>
                      <Pencil className="w-4 h-4 text-[#64748B]" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(cat.category_id)} data-testid={`delete-cat-${cat.category_id}`}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {activeCats.length === 0 && (
              <Card className="card-default">
                <CardContent className="py-12 text-center">
                  <Layers className="w-10 h-10 text-[#64748B] mx-auto mb-3 opacity-50" />
                  <p className="text-[#64748B]">No hay categorias activas</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Inactive categories */}
          {inactiveCats.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-[#94A3B8] uppercase mb-3">Inactivas</h2>
              <div className="space-y-2">
                {inactiveCats.map((cat) => (
                  <Card key={cat.category_id} className="card-default opacity-60">
                    <CardContent className="p-3 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-[#64748B]">{cat.name}</p>
                        <p className="text-xs text-[#94A3B8]">
                          {cat.min_index == null ? "≤" : `${fmtHi(cat.min_index)} —`} {cat.max_index == null ? "sin limite" : fmtHi(cat.max_index)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleToggle(cat)} className="text-xs text-[#1B4D3E]">
                          Activar
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(cat.category_id)}>
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar categoria?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta accion eliminara la categoria. Los jugadores que la usaban se reclasificaran segun las categorias activas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-cat-btn">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function CategoryForm({ category, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: category?.name || "",
    min_index: category?.min_index != null ? String(category.min_index) : "",
    max_index: category?.max_index != null ? String(category.max_index) : "",
    is_senior: !!category?.is_senior,
    is_ladies: !!category?.is_ladies,
    tees: Array.isArray(category?.tees) ? category.tees : [],
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const toggleTee = (key) => setForm((prev) => ({
    ...prev,
    tees: prev.tees.includes(key) ? prev.tees.filter((t) => t !== key) : [...prev.tees, key],
  }));

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Nombre requerido");
    setSaving(true);
    try {
      const url = category ? `${API}/club/categories/${category.category_id}` : `${API}/club/categories`;
      const method = category ? "PUT" : "POST";
      const payload = {
        name: form.name.trim(),
        min_index: form.min_index.trim() || null,
        max_index: form.max_index.trim() || null,
        is_senior: !!form.is_senior,
        is_ladies: !!form.is_ladies,
        tees: form.tees,
      };
      const res = await fetch(url, { method, credentials: "include", headers: AUTH(), body: JSON.stringify(payload) });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Error");
      }
      toast.success(category ? "Categoria actualizada" : "Categoria creada");
      onSaved();
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" data-testid="category-form-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-bold text-[#0F172A]">{category ? "Editar Categoria" : "Nueva Categoria"}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#64748B]" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Nombre *</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder='Ej: "Premier"' data-testid="cat-form-name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Index Minimo</Label>
              <Input value={form.min_index} onChange={(e) => set("min_index", e.target.value)} placeholder="Ej: +1.1 o 5.0" data-testid="cat-form-min" />
              <p className="text-xs text-[#94A3B8]">Usa "+" para plus handicap. Vacio = sin limite inferior</p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Index Maximo</Label>
              <Input value={form.max_index} onChange={(e) => set("max_index", e.target.value)} placeholder="Ej: 3.9" data-testid="cat-form-max" />
              <p className="text-xs text-[#94A3B8]">Usa "+" para plus handicap. Vacio = sin limite superior</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="cat-form-senior-toggle"
              onClick={() => set("is_senior", !form.is_senior)}
              className={`flex items-center justify-between px-3 py-2 rounded-md border text-sm ${form.is_senior ? "bg-[#49607A] text-white border-[#49607A]" : "bg-white text-[#0F172A] border-gray-200"}`}
            >
              <span>Senior</span>
              <span className={`w-9 h-5 rounded-full flex items-center transition-colors ${form.is_senior ? "bg-white/25 justify-end" : "bg-gray-200 justify-start"} px-0.5`}>
                <span className={`w-4 h-4 rounded-full ${form.is_senior ? "bg-white" : "bg-white shadow"}`} />
              </span>
            </button>
            <button
              type="button"
              data-testid="cat-form-ladies-toggle"
              onClick={() => set("is_ladies", !form.is_ladies)}
              className={`flex items-center justify-between px-3 py-2 rounded-md border text-sm ${form.is_ladies ? "bg-[#CBB68B] text-[#0B1D33] border-[#CBB68B]" : "bg-white text-[#0F172A] border-gray-200"}`}
            >
              <span>Damas</span>
              <span className={`w-9 h-5 rounded-full flex items-center transition-colors ${form.is_ladies ? "bg-white/40 justify-end" : "bg-gray-200 justify-start"} px-0.5`}>
                <span className={`w-4 h-4 rounded-full ${form.is_ladies ? "bg-[#0B1D33]" : "bg-white shadow"}`} />
              </span>
            </button>
          </div>
          <p className="text-xs text-[#94A3B8] -mt-2">Activa uno o ambos si la categoria filtra por perfil</p>
          <div className="space-y-2 pt-2 border-t border-gray-100">
            <Label className="text-sm font-medium">Bolas de salida</Label>
            <div className="flex flex-wrap gap-2">
              {TEE_COLORS.map((t) => {
                const active = form.tees.includes(t.key);
                return (
                  <button
                    key={t.key}
                    type="button"
                    data-testid={`cat-form-tee-${t.key}`}
                    onClick={() => toggleTee(t.key)}
                    className="text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full transition-all"
                    style={{
                      backgroundColor: active ? t.bg : "transparent",
                      color: active ? t.fg : "#64748B",
                      border: `1.5px solid ${active ? t.bg : t.border || t.bg}`,
                      opacity: active ? 1 : 0.55,
                    }}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-[#94A3B8]">Selecciona todas las bolas que esta categoria puede jugar</p>
          </div>
        </div>
        <div className="px-6 pb-6 flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancelar</Button>
          <Button className="flex-1 bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={handleSave} disabled={saving} data-testid="cat-form-save-btn">
            {saving ? "Guardando..." : category ? "Guardar" : "Crear"}
          </Button>
        </div>
      </div>
    </div>
  );
}
